import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar, Download, Loader2, MoreVertical, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

type ApiCustomerRecord = {
  id: number | null;
  earn_code: string | null;
  free_shirt_quota: number | string | null;
  customer_id: number | string | null;
  updated_at: string | null;
};

type CustomerListResponse = {
  count: number;
  page: number;
  page_size: number;
  results: ApiCustomerRecord[];
};

type CustomerHistoryRow = {
  id: number | null;
  order_at: string | null;
  customer_id: number | string | null;
  order_id: number | null;
  debit: number | string | null;
  credit: number | string | null;
  created_until: string | null;
  authorized: boolean;
  new_earn_quota: number | string | null;
  previous_quota: number | string | null;
  event_remark: string | null;
};

type CustomerHistoryResponse = {
  customer: ApiCustomerRecord | null;
  count: number;
  results: CustomerHistoryRow[];
};

function parseListResponse(raw: unknown, fallbackPage: number, fallbackPageSize: number): CustomerListResponse {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const count = Number(obj.count ?? 0);
  const page = Number(obj.page ?? fallbackPage);
  const pageSize = Number(obj.page_size ?? fallbackPageSize);
  const rowsRaw = Array.isArray(obj.results) ? obj.results : [];

  const results = rowsRaw.map((item) => {
    const row = item as Record<string, unknown>;
    const idRaw = Number(row.id);
    return {
      id: Number.isFinite(idRaw) ? idRaw : null,
      earn_code: row.earn_code == null ? null : String(row.earn_code),
      free_shirt_quota: row.free_shirt_quota == null ? null : (row.free_shirt_quota as number | string),
      customer_id: row.customer_id == null ? null : (row.customer_id as number | string),
      updated_at: row.updated_at == null ? null : String(row.updated_at),
    } as ApiCustomerRecord;
  });

  return {
    count: Number.isFinite(count) ? count : 0,
    page: Number.isFinite(page) && page > 0 ? page : fallbackPage,
    page_size: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : fallbackPageSize,
    results,
  };
}

function formatUpdatedAt(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function parseHistoryResponse(raw: unknown): CustomerHistoryResponse {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const customerRaw = (obj.customer ?? null) as Record<string, unknown> | null;
  const resultsRaw = Array.isArray(obj.results) ? obj.results : [];

  const customer: ApiCustomerRecord | null = customerRaw
    ? {
        id: Number.isFinite(Number(customerRaw.id)) ? Number(customerRaw.id) : null,
        earn_code: customerRaw.earn_code == null ? null : String(customerRaw.earn_code),
        free_shirt_quota:
          customerRaw.free_shirt_quota == null ? null : (customerRaw.free_shirt_quota as number | string),
        customer_id: customerRaw.customer_id == null ? null : (customerRaw.customer_id as number | string),
        updated_at: customerRaw.update_at == null ? null : String(customerRaw.update_at),
      }
    : null;

  const results: CustomerHistoryRow[] = resultsRaw.map((item) => {
    const row = item as Record<string, unknown>;
    return {
      id: Number.isFinite(Number(row.id)) ? Number(row.id) : null,
      order_at: row.order_at == null ? null : String(row.order_at),
      customer_id: row.customer_id == null ? null : (row.customer_id as number | string),
      order_id: Number.isFinite(Number(row.order_id)) ? Number(row.order_id) : null,
      debit: row.debit == null ? null : (row.debit as number | string),
      credit: row.credit == null ? null : (row.credit as number | string),
      created_until: row.created_until == null ? null : String(row.created_until),
      authorized: Boolean(row.authorized),
      new_earn_quota: row.new_earn_quota == null ? null : (row.new_earn_quota as number | string),
      previous_quota: row.previous_quota == null ? null : (row.previous_quota as number | string),
      event_remark: row.event_remark == null ? null : String(row.event_remark),
    };
  });

  return {
    customer,
    count: Number.isFinite(Number(obj.count)) ? Number(obj.count) : results.length,
    results,
  };
}

export function CustomerEarnPage() {
  const [searchCustomerId, setSearchCustomerId] = useState('');
  const [searchEarnCode, setSearchEarnCode] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('25');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<CustomerHistoryResponse | null>(null);
  const [historyForCustomer, setHistoryForCustomer] = useState<string | number | null>(null);
  const [data, setData] = useState<CustomerListResponse>({
    count: 0,
    page: 1,
    page_size: 25,
    results: [],
  });

  const totalPages = useMemo(() => {
    if (!data.page_size || data.page_size <= 0) return 1;
    return Math.max(1, Math.ceil(data.count / data.page_size));
  }, [data.count, data.page_size]);

  async function loadRecords(params?: { nextPage?: number; customerId?: string; earnCode?: string; nextPageSize?: number }) {
    const targetPage = params?.nextPage ?? page;
    const ps = params?.nextPageSize ?? Number(pageSize);
    const effectivePageSize = Number.isFinite(ps) && ps > 0 ? ps : 25;
    const customerIdFilter = params?.customerId ?? searchCustomerId;
    const earnCodeFilter = params?.earnCode ?? searchEarnCode;

    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('page', String(targetPage));
      qs.set('page_size', String(effectivePageSize));
      if (customerIdFilter.trim()) qs.set('customer_id', customerIdFilter.trim());
      if (earnCodeFilter.trim()) qs.set('earn_code', earnCodeFilter.trim());

      const resp = await fetch(`/v1/api/earn-code-manager/customers/?${qs.toString()}`, { method: 'GET' });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const raw = (await resp.json()) as unknown;
      const parsed = parseListResponse(raw, targetPage, effectivePageSize);
      setData(parsed);
      setPage(parsed.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load customer records.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadRecords({ nextPage: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSearchSubmit() {
    void loadRecords({ nextPage: 1 });
  }

  async function onExportCsv() {
    if (exporting) return;
    setExporting(true);
    setError(null);
    try {
      const resp = await fetch('/v1/api/earn-code-manager/export-customer/', { method: 'GET' });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Export failed (${resp.status})${text ? `: ${text}` : ''}`);
      }

      const blob = await resp.blob();
      const fileName = 'prod_customer_export.csv';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to export CSV.');
    } finally {
      setExporting(false);
    }
  }

  async function onViewHistory(record: ApiCustomerRecord) {
    if (!record.id) {
      setError('Unable to open history: missing customer id.');
      return;
    }
    setHistoryForCustomer(record.customer_id ?? record.id);
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError(null);
    setHistoryData(null);
    try {
      const resp = await fetch(`/v1/api/earn-code-manager/history/${record.id}/`, { method: 'GET' });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`History request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const raw = (await resp.json()) as unknown;
      setHistoryData(parseHistoryResponse(raw));
    } catch (e) {
      setHistoryError(e instanceof Error ? e.message : 'Failed to load history.');
    } finally {
      setHistoryLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Search Card */}
      <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <CardHeader>
          <CardTitle className="text-base text-gray-900 dark:text-neutral-50">Search Records</CardTitle>
          <CardDescription className="text-gray-600 dark:text-neutral-400">Search by customer ID and earn code</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-neutral-500" />
              <Input
                placeholder="Customer ID"
                className="pl-9 bg-gray-100 dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 text-gray-900 dark:text-neutral-50 placeholder:text-gray-600 dark:placeholder:text-neutral-400"
                value={searchCustomerId}
                onChange={(e) => setSearchCustomerId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSearchSubmit();
                }}
              />
            </div>
            <Input
              placeholder="Earn Code"
              className="bg-gray-100 dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 text-gray-900 dark:text-neutral-50 placeholder:text-gray-600 dark:placeholder:text-neutral-400"
              value={searchEarnCode}
              onChange={(e) => setSearchEarnCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearchSubmit();
              }}
            />
            <div className="flex gap-2">
              <Button onClick={onSearchSubmit} className="flex-1" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchCustomerId('');
                  setSearchEarnCode('');
                  void loadRecords({ nextPage: 1, customerId: '', earnCode: '' });
                }}
                disabled={loading}
              >
                Reset
              </Button>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-neutral-400">Rows per page</span>
            <Select
              value={pageSize}
              onValueChange={(value) => {
                setPageSize(value);
                setPage(1);
                const nextPageSize = Number(value);
                void loadRecords({ nextPage: 1, nextPageSize: Number.isFinite(nextPageSize) ? nextPageSize : 25 });
              }}
            >
              <SelectTrigger className="h-9 w-[100px]">
                <SelectValue placeholder="25" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customer Records Card */}
      <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base text-gray-900 dark:text-neutral-50">Customer Earn</CardTitle>
            <CardDescription className="text-gray-600 dark:text-neutral-400">
              Showing {data.results.length} of {data.count} record(s)
            </CardDescription>
          </div>
          <Button variant="outline" onClick={() => void onExportCsv()} disabled={exporting} className="h-9">
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
          ) : null}
          <div className="border border-gray-200 dark:border-neutral-800 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 dark:border-neutral-800 bg-gray-100 dark:bg-neutral-900/50">
                  <TableHead className="px-4 py-3 text-gray-900 dark:text-neutral-50">Customer ID</TableHead>
                  <TableHead className="px-4 py-3 text-gray-900 dark:text-neutral-50">Earn Code</TableHead>
                  <TableHead className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50">Quota</TableHead>
                  <TableHead className="px-4 py-3 text-gray-900 dark:text-neutral-50">Updated At</TableHead>
                  <TableHead className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 dark:text-neutral-400 py-8">
                      <div className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading records...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : data.results.length > 0 ? (
                  data.results.map((record, idx) => (
                    <TableRow key={`${record.customer_id ?? 'na'}-${record.earn_code ?? 'na'}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-neutral-900/20">
                      <TableCell className="px-4 py-3 text-gray-900 dark:text-neutral-50 font-mono">
                        {record.customer_id ?? '-'}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-gray-900 dark:text-neutral-50 font-mono">
                        {record.earn_code ?? '-'}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50">
                        {record.free_shirt_quota ?? 0}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-gray-900 dark:text-neutral-50">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3 text-gray-400 dark:text-neutral-500 flex-shrink-0" />
                          <span className="text-sm">{formatUpdatedAt(record.updated_at)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => void onViewHistory(record)}>View History</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 dark:text-neutral-400 py-8">
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-neutral-400">
              Page {data.page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={loading || data.page <= 1}
                onClick={() => void loadRecords({ nextPage: data.page - 1 })}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={loading || data.page >= totalPages}
                onClick={() => void loadRecords({ nextPage: data.page + 1 })}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Customer History</DialogTitle>
            <DialogDescription>
              {historyForCustomer == null ? 'Transaction history' : `Customer ID: ${historyForCustomer}`}
            </DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-8 text-center text-sm text-gray-600 dark:text-neutral-400">
              <div className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading transaction history...
              </div>
            </div>
          ) : historyError ? (
            <div className="text-sm text-red-700 dark:text-red-300">{historyError}</div>
          ) : !historyData || historyData.results.length === 0 ? (
            <div className="text-sm text-gray-600 dark:text-neutral-400">No transaction history found.</div>
          ) : (
            <div className="max-h-[60vh] overflow-auto rounded-md border border-gray-200 dark:border-neutral-800">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order At</TableHead>
                    <TableHead>Order ID</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Prev Quota</TableHead>
                    <TableHead className="text-right">New Quota</TableHead>
                    <TableHead>Authorized</TableHead>
                    <TableHead>Remark</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyData.results.map((row, idx) => (
                    <TableRow key={`${row.id ?? 'na'}-${idx}`}>
                      <TableCell className="text-sm">{formatUpdatedAt(row.order_at)}</TableCell>
                      <TableCell className="text-sm font-mono">{row.order_id ?? '-'}</TableCell>
                      <TableCell className="text-right text-sm">{row.debit ?? 0}</TableCell>
                      <TableCell className="text-right text-sm">{row.credit ?? 0}</TableCell>
                      <TableCell className="text-right text-sm">{row.previous_quota ?? 0}</TableCell>
                      <TableCell className="text-right text-sm">{row.new_earn_quota ?? 0}</TableCell>
                      <TableCell className="text-sm">{row.authorized ? 'Yes' : 'No'}</TableCell>
                      <TableCell className="text-sm max-w-[260px] truncate">{row.event_remark || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
